import { useShowAlert } from '~/composables/use-show-alert'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useState } from '#app'

export function useAgreementVideo(
    videoId: string | null,
    allowSkippingWistiaVideo: boolean,
    validateContinue: () => boolean,
    handleContinue: () => void
) {
    // This is so if they cancel out of the login modal, they do not have to watch the video again
    let alreadyWatchedVideo = false

    // Keep track of when the popover is shown
    const isWatchingVideo = useState('isWatchingWistiaVideo', () => false)
    isWatchingVideo.value = false

    // No type definitions for wistia video player
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let wistiaVideoPlayer: any = undefined
    const wisitiaConfig = {
        id: videoId,
        onReady: function (video: unknown) {
            wistiaVideoPlayer = video
            // Mark watched 20 seconds before end of video
            video.bind(
                'crosstime',
                Number(Math.max(5, Math.floor(video.duration()) - 20)),
                function () {
                    alreadyWatchedVideo = true
                }
            )
            wistiaVideoPlayer.bind('end', function () {
                wistiaVideoPlayer.popover.hide()
            })
            wistiaVideoPlayer.bind('popovershow', function () {
                isWatchingVideo.value = true
                // Wait until overlay is shown and colored
                setTimeout(() => {
                    const overlay = document.querySelector(
                        '.wistia_popover_overlay'
                    )
                    if (overlay !== null) {
                        // Clone the overlay to remove event listeners
                        const new_ele = overlay.cloneNode(true)
                        overlay.parentNode?.replaceChild(new_ele, overlay)
                    } else {
                        console.error('No wistia popover overlay found')
                    }
                }, 1000)
            })
            wistiaVideoPlayer.bind('popoverhide', function () {
                if (alreadyWatchedVideo) {
                    handleContinue()
                }
                isWatchingVideo.value = false
                // After removing event listeners it seems we have to close this ourselves
                const overlay = document.querySelector(
                    '.wistia_popover_overlay'
                )
                if (overlay !== null) {
                    overlay.parentNode?.removeChild(overlay)
                }
            })
        }
    }
    if (videoId !== null) {
        // @ts-expect-error : wistia global
        window._wq = window._wq || []
        onMounted(() => {
            // @ts-expect-error : wistia global
            window._wq.push(wisitiaConfig)
        })
        onUnmounted(() => {
            // @ts-expect-error : wistia global
            window._wq.push({ revoke: wisitiaConfig })
        })
    }

    const isLoadingVideo = ref(false)
    let wistiaLoadingIntervalId: number | undefined = undefined
    let wistiaNotLoadingTimeoutId: number | undefined = undefined
    onUnmounted(() => {
        if (wistiaLoadingIntervalId !== undefined) {
            clearInterval(wistiaLoadingIntervalId)
            wistiaLoadingIntervalId = undefined
        }
        if (wistiaNotLoadingTimeoutId !== undefined) {
            clearTimeout(wistiaNotLoadingTimeoutId)
            wistiaNotLoadingTimeoutId = undefined
        }
    })

    const showAlert = useShowAlert()
    async function handleWatchVideo() {
        if (alreadyWatchedVideo) {
            handleContinue()
            return
        }

        if (!validateContinue()) {
            return
        }

        if (wistiaVideoPlayer !== undefined) {
            wistiaVideoPlayer.popover.show()
            return
        }

        // Handle the player not being ready yet, set button to loading and wait
        isLoadingVideo.value = true
        // @ts-expect-error : TS thinks this is node
        wistiaLoadingIntervalId = setInterval(() => {
            // If we now have the player
            if (wistiaVideoPlayer !== undefined) {
                // Stop interval check
                if (wistiaLoadingIntervalId !== undefined) {
                    clearInterval(wistiaLoadingIntervalId)
                    wistiaLoadingIntervalId = undefined
                }

                // Stop timeout check
                if (wistiaNotLoadingTimeoutId !== undefined) {
                    clearTimeout(wistiaNotLoadingTimeoutId)
                    wistiaNotLoadingTimeoutId = undefined
                }

                // Show popover
                wistiaVideoPlayer.popover.show()
                isLoadingVideo.value = false
            }
        }, 30)

        // Wait 5sec and if we still have no video, show an error
        // @ts-expect-error : TS thinks this is node
        wistiaNotLoadingTimeoutId = setTimeout(() => {
            if (wistiaLoadingIntervalId === undefined) {
                clearInterval(wistiaLoadingIntervalId)
                wistiaLoadingIntervalId = undefined
            }
            wistiaNotLoadingTimeoutId = undefined
            showAlert(
                'Heads Up!',
                'Whoops! It appears the video is having trouble loading. Please reload and try again.'
            )
        }, 5000)
    }

    function handleAlreadyWatched() {
        alreadyWatchedVideo = true
        handleContinue()
        if (wistiaVideoPlayer !== undefined) {
            wistiaVideoPlayer.popover.hide()
        }
    }

    const showAlreadyWatchButton = computed(
        () => isWatchingVideo.value && allowSkippingWistiaVideo
    )

    return {
        isLoadingVideo,
        isWatchingVideo,
        showAlreadyWatchButton,
        handleWatchVideo,
        handleAlreadyWatched
    }
}
